import React from "react";
import { FileText, Layers, TrendingUp } from "lucide-react";

/**
 * WhatWeSolve — Three-card value proposition strip. Obsidian cards on
 * dark background with a Liquid Gold left border and icon well.
 */
const GOLD = "#C9A84C";

const CARDS = [
    {
        icon: FileText,
        title: "One Invoice, One Partner",
        body: "Replace 3–5 disconnected vendors with a single infrastructure provider. One relationship, one bill, total clarity.",
        testid: "solve-card-one-invoice",
    },
    {
        icon: Layers,
        title: "Full Infrastructure From Day One",
        body: "Website, CRM, automation, payments, analytics — deployed and connected before you open for business.",
        testid: "solve-card-full-infra",
    },
    {
        icon: TrendingUp,
        title: "Scales With You",
        body: "Start with what you need today. As your business grows, your infrastructure grows with it. No rebuilds, no migrations.",
        testid: "solve-card-scales",
    },
];

export default function WhatWeSolve() {
    return (
        <section
            id="what-we-solve"
            data-testid="what-we-solve-section"
            className="relative py-24 md:py-32"
            style={{ background: "#050505" }}
        >
            <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                <div className="max-w-2xl">
                    <div
                        className="text-[10px] uppercase tracking-[0.4em] mb-4"
                        style={{ color: GOLD, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                        data-testid="solve-overline"
                    >
                        What We Solve
                    </div>
                    <h2
                        className="text-4xl md:text-5xl leading-[1.1] tracking-tight"
                        style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: "#EDEDED" }}
                    >
                        Business Infrastructure as a Service —
                        <span style={{ color: GOLD }}> everything you need, nothing you don&rsquo;t.</span>
                    </h2>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {CARDS.map((c) => (
                        <div
                            key={c.title}
                            data-testid={c.testid}
                            className="relative p-8 transition-all duration-300"
                            style={{
                                background: "#0C0C0C",
                                border: "1px solid rgba(201,168,76,0.14)",
                                borderLeft: `3px solid ${GOLD}`,
                                borderRadius: "10px",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = "rgba(201,168,76,0.35)";
                                e.currentTarget.style.borderLeft = `3px solid ${GOLD}`;
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 12px 28px rgba(201,168,76,0.08)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = "rgba(201,168,76,0.14)";
                                e.currentTarget.style.borderLeft = `3px solid ${GOLD}`;
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                        >
                            <div
                                className="inline-flex items-center justify-center w-12 h-12 rounded-md mb-5"
                                style={{
                                    background: "rgba(201,168,76,0.08)",
                                    border: "1px solid rgba(201,168,76,0.25)",
                                    color: GOLD,
                                }}
                            >
                                <c.icon size={22} strokeWidth={1.6} />
                            </div>
                            <h3
                                className="text-xl mb-3"
                                style={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    color: "#EDEDED",
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                {c.title}
                            </h3>
                            <p
                                className="text-sm leading-relaxed"
                                style={{
                                    fontFamily: "'Inter', 'Montserrat', sans-serif",
                                    color: "#A8A8A8",
                                }}
                            >
                                {c.body}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
